import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics } from './Analytics'

export interface TikTokPixelConfig {
  measurementId?: string
}

export class TikTokPixel extends DniAnalytics<AnalyticsEvents> {
  readonly config: TikTokPixelConfig
  private static instance: TikTokPixel | null = null

  constructor(config: TikTokPixelConfig) {
    super()
    this.config = config
    this.initialize()
  }

  // Static getInstance method for singleton pattern
  public static getInstance(config: TikTokPixelConfig): TikTokPixel {
    if (!TikTokPixel.instance) {
      TikTokPixel.instance = new TikTokPixel(config)
    }
    return TikTokPixel.instance
  }

  protected initialize(): void {
    if (!this.config.measurementId) {
      console.error('[TikTokPixel] measurementId is not defined')
      return
    }
    this.setIsReady(false)
    const head = document.querySelector('head')
    const existingScript = document.querySelector(`script[data-id="tiktok-pixel-${this.config.measurementId}"]`)

    // Confirm only init once
    // Converted textContent is in sc-44553
    if (!existingScript) {
      const script = document.createElement('script')
      script.setAttribute('data-id', `tiktok-pixel-${this.config.measurementId}`)
      script.textContent = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        var ttq = w[t] = w[t] || [];
        ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"], ttq.setAndDefer = function (t, e) {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
          }
        };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function (t) {
          for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++
          )ttq.setAndDefer(e, ttq.methods[n]);
          return e
        }, ttq.load = function (e, n) {
          var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = i, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {};
          var o = document.createElement("script");
          o.type = "text/javascript", o.async = !0, o.src = i + "?sdkid=" + e + "&lib=" + t;
          var a = document.getElementsByTagName("script")[0];
          a.parentNode.insertBefore(o, a)
        };

        ttq.load('${this.config.measurementId}');
        ttq.page();
      }(window, document, 'ttq');
      `
      head?.appendChild(script)
      /**
       * Tiktok Pixel 初始化後本身有 queue 所以可以放心設定成 ready
       * 主要是下面這段
       * for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
       */
      this.setIsReady(true)
    }
    else {
      this.setIsReady(true)
    }
  }

  protected sendAnalyticsEvent(customEvent: BaseAnalyticsEvent) {
    if (!window.ttq) {
      console.error('[TikTokPixel] window.ttq is not defined')
      return
    }

    try {
      const { eventName, eventParams } = customEvent
      window.ttq.track?.(eventName, eventParams)
      return 0
    }
    catch (error) {
      console.error('[TikTokPixel] sendEvent', error)
      return -1
    }
  }
}
